'use client';

import Drawer from '@mui/material/Drawer';
import { Typography, Box, Divider, Paper, Toolbar, Button, Stack, Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { icons } from '@peerless/ui-assets/icons';
import React, { PropsWithChildren } from 'react';
import { ProductTitle, useCart } from '@peerless/ui-shared';

import Image from 'next/image';
import { useFormatter, useTranslations } from 'use-intl';
import { EmptyCartSlideoutBanner } from './EmptyCartSlideoutBanner';

import { RouterLink } from '@/navigation';
import { ProductLink } from '../../products/components/ProductLink';

export function CartSlideoutComponent({ children }: PropsWithChildren) {
  const { cartData, isOpen, setIsOpen, isLoadingCartData } = useCart();
  const t = useTranslations();
  const format = useFormatter();

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: '30rem' } } }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: '0',
          zIndex: '2',
          backgroundColor: 'common.white',
        }}
      >
        <Typography variant={'h3'} className="header-underline" marginBottom={'0'}>
          {t('Header.shopping-cart')}
        </Typography>

        <Button
          variant="text"
          sx={{ color: 'secondary.dark' }}
          onClick={event => {
            event.preventDefault();
            setIsOpen(false);
          }}
        >
          <FontAwesomeIcon icon={icons.close} fixedWidth size="lg" />
        </Button>
      </Toolbar>
      {!cartData || cartData.cartItems.length === 0 ? (
        <EmptyCartSlideoutBanner />
      ) : (
        <Box sx={{ position: 'relative', padding: '1.5rem' }}>
          {cartData?.cartItems.map(item => (
            <React.Fragment key={item.product.sku}>
              <Paper elevation={0} sx={{ marginBottom: '1rem' }}>
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 150,
                      height: 150,
                    }}
                  >
                    <ProductLink sku={item.product.sku} title={item.product.title}>
                      <Image
                        src={item.product.image}
                        alt={item.product.title}
                        fill
                        style={{ objectFit: 'contain' }}
                      />
                    </ProductLink>
                  </Box>
                  <Box sx={{ flex: '1' }}>
                    <ProductTitle sku={item.product.sku} title={item.product.title}>
                      {item.product.title}
                    </ProductTitle>
                    <Typography variant="body2" marginBottom={1}>
                      {item.product.sku}
                    </Typography>

                    <Typography variant="body1" fontWeight={'700'}>
                      {!isLoadingCartData ? (
                        <>
                          {`${t('Cart.quantity')}: ${item.quantity} = `}

                          {format.number(item.lineTotal ?? 0, {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </>
                      ) : (
                        <Skeleton variant="text" />
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              <Divider sx={{ borderWidth: '1px', marginBottom: 2 }} />
            </React.Fragment>
          ))}
          <Box
            sx={{
              paddingBottom: '1rem',
              paddingTop: '1rem',
              position: 'sticky',
              bottom: '0',
              backgroundColor: 'common.white',
            }}
          >
            <Typography variant="h4" textAlign={'right'}>
              {!isLoadingCartData ? (
                <>{`${t('Cart.total')}: ${format.number(cartData?.totalPrice ?? 0, { style: 'currency', currency: 'USD' })}`}</>
              ) : (
                <Skeleton variant="text" />
              )}
            </Typography>
            <Button
              variant="contained"
              component={RouterLink}
              href="/cart"
              onClick={() => setIsOpen(false)}
              fullWidth
            >
              {t('Cart.view-cart')}
            </Button>
          </Box>
          {children}
        </Box>
      )}
    </Drawer>
  );
}
